import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import lmsApi from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

interface LMSLoginProps {
    onLoginSuccess?: () => void;
}

const LMSLogin: React.FC<LMSLoginProps> = ({ onLoginSuccess }) => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState<'PHONE' | 'OTP'>('PHONE');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await lmsApi.post('/auth/send-otp/', { phone_number: phoneNumber });
            toast.success('OTP Sent Successfully');
            setStep('OTP');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await lmsApi.post('/auth/verify-otp/', {
                phone_number: phoneNumber,
                otp: otp
            });
            // Token is now set in HttpOnly cookie by backend
            toast.success('Login Successful');
            if (onLoginSuccess) {
                onLoginSuccess();
            } else {
                navigate('/lms/dashboard');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
            <Card className="w-full max-w-md shadow-lg">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold text-center text-primary">LMS Portal Login</CardTitle>
                    <CardDescription className="text-center">Manage your loans with ease</CardDescription>
                </CardHeader>
                <CardContent>
                    {step === 'PHONE' ? (
                        <form onSubmit={handleSendOtp} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number</Label>
                                <Input
                                    id="phone"
                                    placeholder="+1234567890"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? 'Sending...' : 'Send OTP'}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerifyOtp} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="otp">Enter OTP</Label>
                                <Input
                                    id="otp"
                                    placeholder="123456"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    required
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? 'Verifying...' : 'Verify & Login'}
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full mt-2"
                                onClick={() => setStep('PHONE')}
                                type="button"
                            >
                                Back to Phone
                            </Button>
                        </form>
                    )}
                </CardContent>
                <CardFooter className="justify-center">
                    <p className="text-xs text-gray-400">Powered by Intellend</p>
                </CardFooter>
            </Card>
        </div>
    );
};

export default LMSLogin;
